import { Color3, Effect, MeshBuilder, ShaderMaterial, type Mesh, type Scene } from "babylonjs";

// DCC-style floor grid drawn with a custom shader (ported from the EJC editor):
// only the lines render (fragments between lines are discarded → transparent),
// anti-aliased via screen-space derivatives. Two tiers over a square ground:
//   - minor lines every 1 unit, fainter
//   - major lines every 5 units, brighter
// Used as a spatial reference in non-XR contexts (the demo/editor); kept OFF in
// XR where the real world is the reference.

const DEFAULT_GRID_SIZE = 20;

const VERTEX = "ejGridVertex";
const FRAGMENT = "ejGridFragment";

// Register the shaders once (module load).
Effect.ShadersStore[`${VERTEX}VertexShader`] = `
precision highp float;
attribute vec3 position;
uniform mat4 worldViewProjection;
uniform mat4 world;
varying vec2 vWorldXZ;
void main(void) {
  vec4 wp = world * vec4(position, 1.0);
  vWorldXZ = wp.xz;
  gl_Position = worldViewProjection * vec4(position, 1.0);
}
`;

Effect.ShadersStore[`${FRAGMENT}FragmentShader`] = `
#extension GL_OES_standard_derivatives : enable
precision highp float;
varying vec2 vWorldXZ;
uniform vec3 uLineColor;
uniform vec3 uFillColor;      // dark translucent floor fill
uniform float uMinorScale;   // minor cell size (world units)
uniform float uMajorScale;   // major cell size (world units)
uniform float uMinorOpacity; // faint
uniform float uMajorOpacity; // brighter
uniform float uFillOpacity;  // floor fill alpha (0 = lines only)

// Anti-aliased line coverage: distance (in pixels) to the nearest grid line of
// the given cell size, normalised by the fragment's derivative → ~1 on the line,
// 0 between, with a smooth 1px edge.
float gridFactor(vec2 p, float scale) {
  vec2 coord = p / scale;
  vec2 d = fwidth(coord);
  vec2 g = abs(fract(coord - 0.5) - 0.5) / max(d, vec2(1e-6));
  float line = min(g.x, g.y);
  return 1.0 - clamp(line, 0.0, 1.0);
}

void main(void) {
  float minor = gridFactor(vWorldXZ, uMinorScale) * uMinorOpacity;
  float major = gridFactor(vWorldXZ, uMajorScale) * uMajorOpacity;
  float lineA = clamp(max(minor, major), 0.0, 1.0);
  float a = max(lineA, uFillOpacity);
  if (a < 0.002) discard;
  vec3 color = mix(uFillColor, uLineColor, lineA);
  gl_FragColor = vec4(color, a);
}
`;

export default class CreatorGrid {
  readonly mesh: Mesh;

  constructor(scene: Scene, size: number = DEFAULT_GRID_SIZE) {
    this.mesh = MeshBuilder.CreateGround("Floor_Grid", { width: size, height: size }, scene);
    this.mesh.isPickable = false;

    const material = new ShaderMaterial(
      "grid_mat",
      scene,
      { vertex: VERTEX, fragment: FRAGMENT },
      {
        attributes: ["position"],
        uniforms: ["worldViewProjection", "world", "uLineColor", "uFillColor", "uMinorScale", "uMajorScale", "uMinorOpacity", "uMajorOpacity", "uFillOpacity"],
        needAlphaBlending: true,
      },
    );
    material.setColor3("uLineColor", new Color3(0.6, 0.61, 0.63));
    material.setColor3("uFillColor", new Color3(0.03, 0.035, 0.04)); // near-black floor fill
    material.setFloat("uMinorScale", 1.0);
    material.setFloat("uMajorScale", 5.0);
    material.setFloat("uMinorOpacity", 0.28);
    material.setFloat("uMajorOpacity", 0.85);
    material.setFloat("uFillOpacity", 0.35);
    material.backFaceCulling = false;
    material.forceDepthWrite = false; // don't occlude scene content with the transparent floor.
    this.mesh.material = material;
  }

  setEnabled(on: boolean): void {
    this.mesh.setEnabled(on);
  }

  dispose(): void {
    this.mesh.material?.dispose();
    this.mesh.dispose();
  }
}
